// The middle of a document open on the Studio desk (pen "Studio – the desk",
// "The open document: one frame, the middle by kind"), each the view that
// already exists for its kind:
//  - a material planned but not written: a line pointing at Generate (the
//    brief box above); being written: the B2 skeleton (DeskBriefs.tsx);
//  - a written material (text, lesson, document, quiz): the front one is the editor
//    (the editor host follows the desk's front document, editedMaterial.ts;
//    a quiz's questions, options and correct answers are edited there too);
//    one behind it reads as it is, and comes forward (editable) on a press;
//  - a source: its original, in the source viewer's centre (read-only);
//  - anything else: the file's own facts (the Knowledge bank's file view).
// Video/audio transcripts and image regions are not built yet: a source of
// those kinds shows the source viewer's player or image.

export { StudioDocument };

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { MaterialDocument } from "../../../components/Editor/MaterialDocument/MaterialDocument.js";
import { defaultRenderers } from "../../../components/Editor/renderers.js";
import { fileKindOf } from "../../../components/KnowledgeBank/knowledgeBankModel.js";
import { SourceOriginal } from "../../../components/SourceViewer/SourceOriginal.js";
import type { DeskDocument } from "../../../components/Studio/studioDesk.js";
import {
    PlannedMiddle,
    WritingMiddle,
} from "../../../components/StudioDesk/DeskBriefs.js";
import { docStateOf } from "../../../components/StudioDesk/deskFacts.js";
import { useAppShell } from "../context.js";
import { FileDetail, LoadView, usePortLoad } from "../ResourceDetail.js";
import { EditedDocument } from "./MaterialCentre.js";

const StudioDocument = ({
    doc,
    front,
}: {
    readonly doc: DeskDocument;
    readonly front: boolean;
}): ReactNode => {
    const { ports, studio } = useAppShell();
    switch (doc.kind) {
        case "material": {
            const reading = studio.notes.get(doc.id)?.type === "reading";
            switch (docStateOf(doc)) {
                case "planned":
                    return reading ? (
                        <WritingMiddle reading />
                    ) : (
                        <PlannedMiddle />
                    );
                case "writing":
                    return <WritingMiddle reading={reading} />;
                default:
                    break;
            }
            return front ? (
                // A part list belongs to a container, which opens as a
                // binder, never as a document: nothing to open from here.
                <EditedDocument
                    target={undefined}
                    onOpenPart={() => undefined}
                />
            ) : (
                <MaterialBehind id={doc.id} />
            );
        }
        case "source":
            return <SourceMiddle id={doc.id} />;
        case "other":
            return (
                <FileDetail
                    id={doc.id}
                    title={doc.title}
                    loadFile={ports.loadFile}
                />
            );
        default:
            return (
                <FileDetail
                    id={doc.id}
                    title={doc.title}
                    loadFile={ports.loadFile}
                />
            );
    }
};

// A material behind the front one: as it reads, not editable.
const MaterialBehind = ({ id }: { readonly id: string }): ReactNode => {
    const { t } = useLingui();
    const { ports } = useAppShell();
    const { load, retry } = usePortLoad(ports.loadMaterial, id);
    return (
        <LoadView
            load={load}
            kind="material"
            onRetry={retry}
            render={(loaded) => (
                <MaterialDocument
                    elements={loaded.elements}
                    renderers={defaultRenderers}
                    ariaLabel={t`Material content`}
                />
            )}
        />
    );
};

// A source's original, as the source viewer shows it (read-only).
const SourceMiddle = ({ id }: { readonly id: string }): ReactNode => {
    const { ports } = useAppShell();
    const { load, retry } = usePortLoad(ports.loadSource, id);
    return (
        <LoadView
            load={load}
            kind="source"
            onRetry={retry}
            render={(source) => (
                <SourceOriginal
                    fileKind={fileKindOf(
                        source.meta.filename,
                        source.original?.mediaType,
                    )}
                    source={source}
                />
            )}
        />
    );
};
